import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box } from '@mui/material';

import {
    SimpleButton,
    SimpleTextField,
    ReportScoringBandsLegend,
    Container,
    Loading,
    ErrorMessage,
} from '@/src/ui';
import { apiRequest } from '@/src/services';
import StudentStatusReportDataTable from './components/StudentStatusReportDataTable';
import type { AssignmentStudentDetailDTO } from './models/statusReportDTOs';

// Page for viewing student-level status report details

const StudentStatusReportPage: React.FC = () => {
    document.title = 'Status Report — Student Details';
    // Page state
    const [searchString, setSearchString] = useState('');
    const [data, setData] = useState<Array<AssignmentStudentDetailDTO>>([]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [pendingStudentIds, setPendingStudentIds] = useState<Set<number>>(new Set());
    const [classFreezeLoading, setClassFreezeLoading] = useState(false);

    const [showReportingBands, setShowReportingBands] = useState(false);

    // Read query params for exam assignment and class
    const { examAssignmentId, classId, examName, className, isByExam, examId } = useMemo(() => {
        const params = new URLSearchParams(window.location.search);

        return {
            examAssignmentId: Number(params.get('examAssignmentId') ?? '0'),
            classId: Number(params.get('classId') ?? '0'),
            examName: params.get('examName') ?? '',
            className: params.get('className') ?? '',
            isByExam: (params.get('isByExam') ?? '').toLowerCase() === 'true',
            examId: Number(params.get('examId') ?? '0'),
        };
    }, []);

    // Show class-level freeze button if a classId is provided
    const showClassFreezeButton = classId !== 0 && !isByExam;

    // Determine if all students are frozen
    const allFrozen = useMemo(
        () => data.length > 0 && data.every((r) => r.isTeacherFrozen === true),
        [data]
    );
    const classFreezeLabel = classFreezeLoading
        ? 'Loading...'
        : allFrozen
          ? 'Unfreeze Class'
          : 'Freeze Class';
    const nextClassFrozen = !allFrozen;

    // Load student details
    const fetchData = useCallback(async () => {
        setLoaded(false);
        setError('');

        if (!examAssignmentId || Number.isNaN(examAssignmentId)) {
            setError('Missing examAssignmentId in URL.');
            setLoaded(true);
            return;
        }

        let requestUrl = `reports/statusReport/assignmentDetails?examAssignmentId=${examAssignmentId}`;
        if (classId) requestUrl += `&classId=${classId}`;
        if (isByExam) requestUrl += `&isByExam=true`;

        try {
            const res = await apiRequest<Array<AssignmentStudentDetailDTO>>('GET', requestUrl);
            setData(res.data ?? []);
        } catch {
            setError('Failed to load assignment student details');
        } finally {
            setLoaded(true);
        }
    }, [examAssignmentId, classId, isByExam]);

    // Toggles freeze for a single student
    async function handleToggleFreeze(
        studentUserId: number,
        isFrozen: boolean,
        attemptExamAssignmentId?: number | null
    ) {
        if (!examAssignmentId) return;
        const target = isFrozen;

        const assignmentId = attemptExamAssignmentId ?? examAssignmentId;

        setPendingStudentIds((prev) => new Set(prev).add(studentUserId));
        try {
            await apiRequest('POST', 'reports/statusReport/freeze/student', {
                examAssignmentId: assignmentId,
                studentUserId,
                isFrozen: target,
            });

            const verifyUrl =
                `reports/statusReport/assignmentDetails?examAssignmentId=${examAssignmentId}` +
                (classId ? `&classId=${classId}` : '') +
                (isByExam ? `&isByExam=true` : '') +
                (examId ? `&examId=${examId}` : '');

            const res = await apiRequest<Array<AssignmentStudentDetailDTO>>('GET', verifyUrl);
            const fresh = res.data ?? [];
            const serverRow = fresh.find((r) => r.studentUserId === studentUserId);
            if (serverRow) {
                setData((prev) =>
                    prev.map((r) =>
                        r.studentUserId === studentUserId
                            ? { ...r, isTeacherFrozen: !!serverRow.isTeacherFrozen }
                            : r
                    )
                );
            }
        } catch {
            setError('Failed to toggle freeze for student.');
        } finally {
            setPendingStudentIds((prev) => {
                const next = new Set(prev);
                next.delete(studentUserId);
                return next;
            });
        }
    }

    // Toggles freeze for the entire class
    async function handleToggleFreezeClass() {
        if (!examAssignmentId) return;

        setClassFreezeLoading(true);
        try {
            await apiRequest('POST', 'reports/statusReport/freeze', {
                examAssignmentId,
                isFrozen: nextClassFrozen,
            });

            const res = await apiRequest<Array<AssignmentStudentDetailDTO>>(
                'GET',
                `reports/statusReport/assignmentDetails?examAssignmentId=${examAssignmentId}&classId=${classId}`
            );
            setData(res.data ?? []);
        } catch {
            setError('Failed to toggle freeze for class.');
        } finally {
            setClassFreezeLoading(false);
        }
    }

    // Load data when component mounts
    useEffect(() => {
        (async function init() {
            await fetchData();
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Render page
    return (
        <Container>
            <div>
                <div>
                    <div className="mb-3">
                        <Link to="/reports/statusReport" className="underline hover:no-underline">
                            ← Back to Status Report
                        </Link>
                    </div>

                    <h1 className="page-header">Status Student Details</h1>

                    {/* Exam and class display */}
                    <div>
                        <Box sx={{ p: 1 }}>
                            <div style={{ lineHeight: 1.5 }}>
                                <strong>Exam:</strong> {examName || '-'}
                                {className ? (
                                    <>
                                        {' '}
                                        • <strong>Class:</strong> {className}
                                    </>
                                ) : null}
                            </div>
                        </Box>
                    </div>

                    <Loading visible={!loaded} message="Loading Status Student Details..." />
                    <ErrorMessage
                        error={error}
                        style={{ textAlign: 'center', marginBottom: '8px' }}
                    />

                    {loaded && (
                        <div>
                            {/* Search bar and class freeze button */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 2,
                                }}
                            >
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    {showClassFreezeButton && (
                                        <SimpleButton
                                            onClick={handleToggleFreezeClass}
                                            variant="contained"
                                            color="primary"
                                            disabled={classFreezeLoading}
                                            aria-pressed={allFrozen}
                                            aria-label={classFreezeLabel}
                                        >
                                            {classFreezeLabel}
                                        </SimpleButton>
                                    )}
                                    <SimpleButton
                                        variant="outlined"
                                        onClick={() => setShowReportingBands(!showReportingBands)}
                                        aria-expanded={showReportingBands}
                                        aria-label="Toggle reporting bands dropdown"
                                    >
                                        {showReportingBands
                                            ? 'Hide Scoring Bands'
                                            : 'Show Scoring Bands'}
                                    </SimpleButton>
                                </Box>

                                <SimpleTextField
                                    name="student-status-search"
                                    label="Search"
                                    variant="outlined"
                                    value={searchString}
                                    onChange={(e) => setSearchString(e.target.value)}
                                    sx={{ minWidth: 200, maxWidth: 300 }}
                                />
                            </Box>

                            <Box sx={{ pb: 2, display: 'flex', justifyContent: 'start' }}>
                                <ReportScoringBandsLegend
                                    dataLoaded={loaded}
                                    showLegend={showReportingBands}
                                />
                            </Box>

                            <div>
                                <StudentStatusReportDataTable
                                    data={data}
                                    searchString={searchString}
                                    onToggleFreeze={handleToggleFreeze}
                                    pendingStudentIds={pendingStudentIds}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Container>
    );
};

export default StudentStatusReportPage;
